<template>
    <DashboardContentLayout class="space-y-8" :heading="$t('views.dataQuality.header.heading')" :subheading="$t('views.dataQuality.history.subheading')">
        <template #header-top-right>
            <DataQualityViewSwitch />
        </template>

        <div class="flex items-center gap-x-2 text-sm text-gray-500">
            <Info class="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{{ $t('views.dataQuality.texts.trialNotice') }}</span>
        </div>

        <div class="pt-8 border-t border-t-gray-200 space-y-6">
            <!-- Date navigation card -->
            <div class="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg border border-gray-200 bg-white p-4 border-l-4 border-l-blue-500">
                <CalendarDays class="h-5 w-5 text-blue-500 flex-shrink-0" aria-hidden="true" />
                <div class="flex-1 min-w-0">
                    <div class="flex items-start gap-x-6">
                        <div>
                            <p class="text-xs text-gray-500 mb-1">{{ $t('views.dataQuality.history.viewingDataFor') }}</p>
                            <DatePicker v-model:value="selectedDate" :min="fromDateStr" :max="toDateStr" :disable-dates="DISABLE_NON_MONDAYS" :show-overlay="false" position="below" class="max-w-56" />
                        </div>
                        <div v-if="selectedDateDirectoryScore" class="flex-shrink-0 rounded-lg border border-gray-200 bg-white px-4 py-3 min-w-[7rem]">
                            <p class="text-xs text-gray-500">{{ $t('views.dataQuality.history.qualityScore') }}</p>
                            <p v-if="store.getters.activeDirectoryName" class="text-xs text-gray-400 mb-1 max-w-[12rem] truncate">{{ store.getters.activeDirectoryName }}</p>
                            <p class="text-3xl font-bold text-gray-900 leading-none mb-2">{{ selectedDateDirectoryScore.formatted }}</p>
                            <div class="flex items-center gap-x-1.5">
                                <span :class="['h-2.5 w-2.5 rounded-full flex-shrink-0', selectedDateDirectoryScore.dotClass]"></span>
                                <span :class="['text-xs font-semibold', selectedDateDirectoryScore.textClass]">{{ selectedDateDirectoryScore.categoryName }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-x-1">
                    <button :disabled="!canGoPrev" class="rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors" @click="prevWeek">
                        <ChevronLeft class="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button class="text-sm font-medium text-blue-600 hover:underline px-1.5" @click="goToday">
                        {{ $t('views.dataQuality.history.today') }}
                    </button>
                    <button :disabled="!canGoNext" class="rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors" @click="nextWeek">
                        <ChevronRight class="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
            </div>

            <!-- Directory / Farms tab toggle -->
            <div class="flex border-b border-gray-200">
                <button
                    :class="[
                        'px-4 py-2.5 text-base font-medium transition-colors border-b-2 -mb-px',
                        !showFarms ? 'border-primary-800 text-primary-800' : 'border-transparent text-gray-500 hover:text-gray-700',
                    ]"
                    @click="showFarms = false"
                >
                    {{ $t('views.dataQuality.history.tabs.directory') }}
                </button>
                <button
                    :class="['px-4 py-2.5 text-base font-medium transition-colors border-b-2 -mb-px', showFarms ? 'border-primary-800 text-primary-800' : 'border-transparent text-gray-500 hover:text-gray-700']"
                    @click="showFarms = true"
                >
                    {{ $t('views.dataQuality.history.tabs.farms') }}
                </button>
            </div>

            <!-- Chart card -->
            <div class="rounded-lg border border-gray-200 bg-white p-4">
                <div class="flex items-center gap-x-2 mb-4">
                    <span class="text-sm font-semibold text-gray-700">{{ $t('views.dataQuality.history.chart.title') }}</span>
                    <TooltipProvider :delay-duration="200">
                        <Tooltip>
                            <TooltipTrigger :aria-label="$t('views.dataQuality.history.chart.categoryNote')">
                                <Info class="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                            </TooltipTrigger>
                            <TooltipContent :collision-padding="12" class="max-w-xs">
                                <p>{{ $t('views.dataQuality.history.chart.categoryNote') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <DataQualityHistoryChart
                    v-if="history.length > 0"
                    :history="history"
                    :show-farms="showFarms"
                    :hidden-farm-ids="hiddenFarmIds"
                    :selected-date-str="normalizedSelectedDate"
                    :categories="store.state.dataQualityCategories ?? []"
                    :directory-name="store.getters.activeDirectoryName"
                    @date-selected="onChartDateSelected"
                />
                <div v-else-if="!isLoadingHistory" class="py-8 text-center text-sm text-gray-500">
                    {{ $t('views.dataQuality.history.noHistory') }}
                </div>
            </div>

            <!-- Snapshot section -->
            <div class="rounded-lg border border-gray-200 bg-white p-6">
                <div class="flex items-center gap-x-2 mb-1">
                    <Clock class="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden="true" />
                    <h2 class="text-base font-semibold text-gray-900">
                        {{ $t('views.dataQuality.history.snapshot.title', { date: formattedDate }) }}
                    </h2>
                </div>
                <p class="text-sm text-gray-500 mb-6">{{ $t('views.dataQuality.history.snapshot.subtitle') }}</p>

                <div v-if="isLoadingDetail" class="text-sm text-gray-500">{{ $t('views.dataQuality.history.loading') }}</div>
                <div v-else-if="noDataForDate" class="text-sm text-gray-500">
                    {{ $t('views.dataQuality.history.noDataForDate', { date: formattedDate }) }}
                </div>
                <template v-else-if="farmAssessmentSummaries.length > 0">
                    <ActionModal :initial-step-index="1" :initial-state="actionModalInitialState" :step-config="actionModalStepConfig" :header="actionModalHeader">
                        <template #default="{ open }">
                            <div class="space-y-3">
                                <ExpansionPanel v-for="summary in farmAssessmentSummaries" :key="summary.id">
                                    <template #header>
                                        <div class="flex items-center gap-x-4 w-full">
                                            <DataQualityStatusBarTooltip :quality-assessment-id="summary.quality_assessment_id" />
                                            <Icon :src="IconSource.FarmsPrimary" class="h-7 w-7 flex-shrink-0" aria-hidden="true" />
                                            <button
                                                v-if="showFarms"
                                                :class="['text-left font-medium flex-1 min-w-0 truncate transition-opacity', hiddenFarmIds.includes(summary.farm.id) ? 'opacity-40' : '']"
                                                @click.stop="toggleFarm(summary.farm.id)"
                                            >
                                                {{ summary.farm.name }}
                                            </button>
                                            <span v-else class="font-medium flex-1 min-w-0 truncate">{{ summary.farm.name }}</span>
                                            <span :class="['inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold flex-shrink-0', qualityBadgeClass(summary.quality_assessment_id)]">
                                                {{ qualityLabel(summary.quality_assessment_id) }}
                                            </span>
                                            <span class="text-sm font-semibold text-gray-700 flex-shrink-0 min-w-[4rem] text-right">
                                                {{ $t('views.dataQuality.history.snapshot.score', { score: formatScore(summary.score) }) }}
                                            </span>
                                        </div>
                                    </template>
                                    <ul class="divide-y divide-gray-100">
                                        <li v-for="item in summary._items" :key="`method-${summary.id}-${item.id}`" class="relative hover:bg-gray-100 transition-colors flex items-center">
                                            <DataQualityStatusBarTooltip :quality-assessment-id="item.quality_assessment_id" class="absolute left-6" />
                                            <button class="flex pl-32 outline-none flex-grow items-center justify-between space-x-2 pr-6 py-6" @click="openForm(item.id, open)">
                                                <div>{{ item._method?.name }}</div>
                                                <Icon :src="IconSource.Right" class="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                            </button>
                                        </li>
                                    </ul>
                                </ExpansionPanel>
                            </div>
                        </template>
                    </ActionModal>
                </template>
            </div>

            <!-- Footer footnote -->
            <p v-if="normalizedSelectedDate" class="text-xs text-gray-400">
                {{ $t('views.dataQuality.history.footer', { date: formattedDate }) }}
            </p>
        </div>
    </DashboardContentLayout>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { CalendarDays, ChevronLeft, ChevronRight, Info, Clock } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import ExpansionPanel from '@/components/ui/ExpansionPanel.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Icon from '@/components/icons/Icon.vue';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import DataQualityStatusBarTooltip from '@/components/common/dataQuality/DataQualityStatusBarTooltip.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { QUALITY_CATEGORY_STYLES } from '@/components/common/dataQuality/qualityCategoryStyles';
import DataQualityHistoryChart from './DataQualityHistoryChart.vue';
import DataQualityViewSwitch from './DataQualityViewSwitch.vue';
import { DATA_QUALITY_ASSESSMENT_FORM_ID } from './constants';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { MutationType } from '@/plugins/store/mutations';
import { DataQualityAssessmentFarmSummary, DataQualityAssessmentMethod, IconSource, DataQualityAssessmentFarmLocationMethodAggregate, MultiStepFormState, Form } from '@/types';

function toLocalDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nearestMondayOnOrBefore(date: Date): string {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return toLocalDateStr(d);
}

const toDate = new Date();
const fromDate = new Date(toDate);
fromDate.setMonth(fromDate.getMonth() - 6);
const toDateStr = toLocalDateStr(toDate);
const fromDateStr = toLocalDateStr(fromDate);

const DISABLE_NON_MONDAYS = [(date: Date) => date.getDay() !== 1];

// selectedDate may be a YYYY-MM-DD string or a full ISO string from the DatePicker
const selectedDate = ref<string>(nearestMondayOnOrBefore(toDate));

// Normalized to YYYY-MM-DD (local). DatePicker may emit a full ISO string.
const normalizedSelectedDate = computed(() => {
    if (!selectedDate.value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(selectedDate.value)) return selectedDate.value;
    return toLocalDateStr(new Date(selectedDate.value));
});

const formattedDate = computed(() => {
    if (!normalizedSelectedDate.value) return '';
    const [year, month, day] = normalizedSelectedDate.value.split('-').map(Number);
    return i18n.global.d(new Date(year, month - 1, day), 'middle');
});

const canGoPrev = computed(() => {
    if (!normalizedSelectedDate.value) return false;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    return toLocalDateStr(new Date(y, m - 1, d - 7)) >= fromDateStr;
});

const canGoNext = computed(() => {
    if (!normalizedSelectedDate.value) return false;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    return toLocalDateStr(new Date(y, m - 1, d + 7)) <= toDateStr;
});

function prevWeek() {
    if (!canGoPrev.value) return;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    selectedDate.value = toLocalDateStr(new Date(y, m - 1, d - 7));
}

function nextWeek() {
    if (!canGoNext.value) return;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    selectedDate.value = toLocalDateStr(new Date(y, m - 1, d + 7));
}

function goToday() {
    selectedDate.value = nearestMondayOnOrBefore(new Date());
}

const showFarms = ref(false);
const hiddenFarmIds = ref<number[]>([]);
const isLoadingHistory = ref(false);
const isLoadingDetail = ref(false);
const noDataForDate = ref(false);

const selectedDateDirectoryScore = computed(() => {
    const assessment = store.state.dataQualityAssessmentForDate;
    if (!assessment || isLoadingDetail.value || noDataForDate.value) return null;
    const id = assessment.quality_assessment_id;
    const key = store.state.dataQualityCategories?.find((c) => c.id === id)?.key ?? '';
    const styles = QUALITY_CATEGORY_STYLES[key];
    return {
        formatted: formatScore(assessment.score),
        categoryName: qualityLabel(id),
        dotClass: styles?.dot ?? 'bg-gray-400',
        textClass: styles?.text ?? 'text-gray-600',
    };
});

const history = computed(() => store.state.dataQualityHistory ?? []);

isLoadingHistory.value = true;
store.dispatch(ActionType.GetDataQualityHistory, { from: fromDateStr, to: toDateStr }).finally(() => {
    isLoadingHistory.value = false;
});

store.dispatch(ActionType.GetDataQualityCategories);
store.dispatch(ActionType.GetDataQualityAssessmentMethods);

let loadDetailSeq = 0;

async function loadDetailForDate(dateStr: string) {
    const seq = ++loadDetailSeq;
    isLoadingDetail.value = true;
    noDataForDate.value = false;
    store.commit(MutationType.SetDataQualityAssessmentForDate, null);
    try {
        const result = await store.dispatch(ActionType.GetDataQualityAssessmentForDate, { date: dateStr, options: { cacheResponse: false, ignoreCache: true } });
        if (seq !== loadDetailSeq) return;
        store.commit(MutationType.SetDataQualityAssessmentForDate, result || null);
        noDataForDate.value = !result;
    } catch {
        if (seq !== loadDetailSeq) return;
        noDataForDate.value = true;
    } finally {
        if (seq === loadDetailSeq) isLoadingDetail.value = false;
    }
}

onBeforeUnmount(() => {
    store.commit(MutationType.SetDataQualityAssessmentForDate, null);
});

loadDetailForDate(normalizedSelectedDate.value);

watch(normalizedSelectedDate, (date) => {
    if (date) loadDetailForDate(date);
});

function onChartDateSelected(date: string) {
    selectedDate.value = date;
}

function toggleFarm(farmId: number) {
    const idx = hiddenFarmIds.value.indexOf(farmId);
    if (idx === -1) {
        hiddenFarmIds.value = [...hiddenFarmIds.value, farmId];
    } else {
        hiddenFarmIds.value = hiddenFarmIds.value.filter((id) => id !== farmId);
    }
}

function qualityBadgeClass(qualityAssessmentId: number): string {
    const key = store.state.dataQualityCategories?.find((c) => c.id === qualityAssessmentId)?.key ?? '';
    return QUALITY_CATEGORY_STYLES[key]?.badge ?? 'bg-gray-100 text-gray-700';
}

function qualityLabel(qualityAssessmentId: number): string {
    return store.state.dataQualityCategories?.find((c) => c.id === qualityAssessmentId)?.name ?? '';
}

function formatScore(score: number | null | undefined): string {
    if (score == null) return '—';
    return score.toFixed(1);
}

interface PopulatedDataQualityAssessmentMethod extends DataQualityAssessmentFarmLocationMethodAggregate {
    _method: DataQualityAssessmentMethod | undefined;
}
interface PopulatedDataQualityAssessmentFarmSummary extends DataQualityAssessmentFarmSummary {
    _items: PopulatedDataQualityAssessmentMethod[];
}

const farmAssessmentSummaries = computed<PopulatedDataQualityAssessmentFarmSummary[]>(() => {
    const assessment = store.state.dataQualityAssessmentForDate;
    if (!assessment || !store.state.dataQualityCategories || !store.state.dataQualityAssessmentMethods) {
        return [];
    }
    return (
        assessment.farm_summaries?.map((f) => ({
            ...f,
            _items:
                f.method_aggregate
                    ?.map((m) => ({
                        ...m,
                        _method: store.state.dataQualityAssessmentMethods!.find((dm) => dm.id === m.assessment_method_id),
                    }))
                    .filter((m) => m._method) || [],
        })) || []
    );
});

const actionModalInitialState = ref<MultiStepFormState>({ form: null, confirmed: false, event: null });
const actionModalStepConfig = ref([{}, { deleteButton: false, editable: false }, { disableRefetch: true, submitActionType: ActionType.GetForm }, {}]);
const actionModalHeader = ref<{ icon_url: string; text: string } | null>(null);
const isLoadingActionForm = ref(false);

async function openForm(methodId: DataQualityAssessmentFarmLocationMethodAggregate['id'], open: CallableFunction) {
    try {
        if (isLoadingActionForm.value) return;
        isLoadingActionForm.value = true;
        const form: Form = await store.dispatch(ActionType.GetForm, {
            form: { form_id: DATA_QUALITY_ASSESSMENT_FORM_ID, resource_id: methodId },
        });
        actionModalInitialState.value = { ...actionModalInitialState.value, form };
        actionModalHeader.value = { icon_url: IconSource.DataQualityIcon as string, text: form.sections?.[0]?.name || '' };
        open();
    } catch {
        //
    } finally {
        isLoadingActionForm.value = false;
    }
}
</script>
